import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, Zap } from 'lucide-react';
import { profile } from '../../data/mock';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Stats', href: '#stats' },
  { label: 'Quest Log', href: '#quests' },
  { label: 'Skill Tree', href: '#skills' },
  { label: 'Achievements', href: '#achievements' },
  { label: 'Off-Duty', href: '#offduty' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const xpPercent = Math.round((profile.xp / profile.maxXp) * 100);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#08080d]/90 backdrop-blur-xl border-b border-[#1a1a2e]/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo / Name */}
            <a href="#" className="flex items-center gap-3 group">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 flex items-center justify-center font-mono text-emerald-400 text-sm font-bold group-hover:border-emerald-400/60 transition-colors">
                TS
              </div>
              <span className="font-mono text-sm text-zinc-300 hidden sm:block">
                <span className="text-emerald-400">teja</span>
                <span className="text-zinc-600">@</span>
                <span className="text-zinc-400">dev</span>
                <span className="text-zinc-600 animate-pulse">_</span>
              </span>
            </a>

            {/* XP bar - center */}
            <div className="hidden md:flex items-center gap-3 flex-1 max-w-xs mx-8">
              <Zap size={14} className="text-amber-400" />
              <div className="flex-1 h-2 rounded-full bg-[#1a1a2e] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${xpPercent}%` }}
                  transition={{ duration: 1.5, delay: 0.5, ease: 'easeOut' }}
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
                />
              </div>
              <span className="font-mono text-xs text-amber-400/80">
                {profile.xp.toLocaleString()} XP
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="px-3 py-2 text-sm text-zinc-400 hover:text-emerald-400 font-mono transition-colors relative group"
                >
                  <span className="text-zinc-600 group-hover:text-emerald-600 transition-colors">{'//'}</span>
                  {' '}{link.label}
                </a>
              ))}
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-zinc-400 hover:text-emerald-400 transition-colors"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#08080d]/98 backdrop-blur-2xl pt-20 px-6"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-center gap-3 px-4 py-4 text-lg text-zinc-300 hover:text-emerald-400 font-mono border-b border-[#1a1a2e]/40 transition-colors group"
                >
                  <ChevronRight size={16} className="text-emerald-600 group-hover:text-emerald-400 transition-colors" />
                  {link.label}
                </motion.a>
              ))}
            </div>
            {/* Mobile XP */}
            <div className="mt-8 flex items-center gap-3">
              <Zap size={14} className="text-amber-400" />
              <div className="flex-1 h-2 rounded-full bg-[#1a1a2e] overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400" style={{ width: `${xpPercent}%` }} />
              </div>
              <span className="font-mono text-xs text-amber-400/80">{profile.xp.toLocaleString()} XP</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
